import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Currency, CurrencyConfig } from '@/types';
import { USD_FALLBACK } from '@/lib/currency/convert';

interface CurrencyState {
    currencies: Currency[];
    selectedCode: string;
    // True only once the visitor explicitly picks a currency in the switcher.
    // Without it, the initial USD fallback is indistinguishable from a real
    // choice, and the admin-chosen default would never take effect.
    hasUserSelected: boolean;

    // Actions
    applyConfig: (config: CurrencyConfig) => void;
    setCurrency: (code: string) => void;

    // Computed
    getSelected: () => Currency;
}

export const useCurrencyStore = create<CurrencyState>()(
    persist(
        (set, get) => ({
            currencies: [USD_FALLBACK],
            selectedCode: USD_FALLBACK.code,
            hasUserSelected: false,

            // Fresh server config replaces the persisted list — rates may have
            // changed since the last visit. Keeps the remembered selection only
            // when the visitor explicitly chose it (and it's still enabled);
            // everyone else gets the admin-chosen default.
            applyConfig: (config: CurrencyConfig) => {
                set((state) => {
                    const keepSelection =
                        state.hasUserSelected &&
                        config.currencies.some((c) => c.code === state.selectedCode);
                    return {
                        currencies: config.currencies,
                        selectedCode: keepSelection ? state.selectedCode : config.defaultCode,
                    };
                });
            },

            setCurrency: (code: string) => {
                if (!get().currencies.some((c) => c.code === code)) return;
                set({ selectedCode: code, hasUserSelected: true });
            },

            getSelected: () => {
                const { currencies, selectedCode } = get();
                return currencies.find((c) => c.code === selectedCode) ?? USD_FALLBACK;
            },
        }),
        {
            name: 'sirius-currency',
            // Shop pages are ISR-cached, so server HTML always shows the base
            // currency. Skipping automatic hydration keeps the first client
            // render identical to that HTML (no hydration mismatch);
            // CurrencyInitializer rehydrates the persisted choice immediately
            // after mount, then refreshes rates from the server.
            skipHydration: true,
        }
    )
);
